import React, { useMemo } from 'react';
import { Activity, Grid3X3, CalendarDays, Clock, Zap, Users, BookOpen, BarChart3, CheckCircle, Target, Award } from 'lucide-react';
import type { LearningEngagement, LoginActivityEntry, EnrollmentTimelineEntry, LearningHoursData, ActivityHeatmapEntry } from '@/services/PagesServices/AnalyticsService';
import { computeLineChart, computeHeatmapGrid, type ComputedInsights } from '../utils';

interface EngagementTabProps {
  engagement: LearningEngagement | null;
  loginActivity: LoginActivityEntry[];
  enrollmentTimeline: EnrollmentTimelineEntry[];
  learningHours: LearningHoursData[];
  activityHeatmap: ActivityHeatmapEntry[];
  insights: ComputedInsights;
}

const EngagementTab: React.FC<EngagementTabProps> = ({
  engagement, loginActivity, enrollmentTimeline,
  learningHours, activityHeatmap, insights,
}) => {
  const dauChartData = useMemo(() => {
    if (loginActivity.length === 0) return null;
    const base = computeLineChart(loginActivity.map(d => ({ value: d.activeUsers, label: d.date })), 560, 180);
    if (!base) return null;
    const points = base.points.map((p, i) => ({ ...p, users: loginActivity[i].activeUsers, date: loginActivity[i].date }));
    return { ...base, points };
  }, [loginActivity]);

  const chartData = useMemo(() => {
    if (!learningHours || learningHours.length === 0) return null;
    const base = computeLineChart(learningHours.map(d => ({ value: d.hours, label: d.week })));
    if (!base) return null;
    const points = base.points.map((p, i) => ({ ...p, hours: learningHours[i].hours, week: learningHours[i].week }));
    return { ...base, points };
  }, [learningHours]);

  const heatmapGrid = useMemo(() => computeHeatmapGrid(activityHeatmap), [activityHeatmap]);

  return (
    <>
      {/* Daily Active Users Chart */}
      {dauChartData && (
        <div className="analytics-section">
          <div className="analytics-section-title">
            <Activity size={20} className="analytics-section-icon" />
            Daily Active Users
          </div>
          <div className="analytics-section-subtitle">Unique user logins per day over the past 30 days</div>
          <div className="analytics-chart-container">
            <svg viewBox={`0 0 ${dauChartData.width} ${dauChartData.height}`} className="analytics-svg-chart" preserveAspectRatio="xMidYMid meet">
              {dauChartData.yLabels.map((label, i) => (
                <g key={i}>
                  <line x1={dauChartData.padding} y1={label.y} x2={dauChartData.padding + dauChartData.chartW} y2={label.y} stroke="var(--color-border, #e5e7eb)" strokeWidth="0.5" strokeDasharray="4,4" />
                  <text x={dauChartData.padding - 8} y={label.y + 4} textAnchor="end" fill="var(--color-secondary-text)" fontSize="10">{label.value}</text>
                </g>
              ))}
              <defs>
                <linearGradient id="dauGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#6366f1" stopOpacity="0.3" />
                  <stop offset="100%" stopColor="#6366f1" stopOpacity="0.02" />
                </linearGradient>
              </defs>
              <path d={dauChartData.areaD} fill="url(#dauGradient)" />
              <path d={dauChartData.pathD} fill="none" stroke="#6366f1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              {dauChartData.points.filter((_, i) => i % 5 === 0 || i === dauChartData.points.length - 1).map((p, i) => (
                <g key={i}>
                  <circle cx={p.x} cy={p.y} r="3.5" fill="#6366f1" stroke="white" strokeWidth="1.5" />
                  <text x={p.x} y={dauChartData.padding + dauChartData.chartH + 16} textAnchor="middle" fill="var(--color-secondary-text)" fontSize="9">
                    {new Date(p.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  </text>
                </g>
              ))}
            </svg>
          </div>
          <div className="analytics-dau-summary">
            <div className="analytics-dau-stat">
              <span className="analytics-dau-stat-value">
                {loginActivity.length > 0 ? Math.round(loginActivity.reduce((s, d) => s + d.activeUsers, 0) / loginActivity.length) : 0}
              </span>
              <span className="analytics-dau-stat-label">Avg Daily Users</span>
            </div>
            <div className="analytics-dau-stat">
              <span className="analytics-dau-stat-value">
                {loginActivity.length > 0 ? Math.max(...loginActivity.map(d => d.activeUsers)) : 0}
              </span>
              <span className="analytics-dau-stat-label">Peak Users</span>
            </div>
            <div className="analytics-dau-stat">
              <span className="analytics-dau-stat-value">
                {loginActivity.length > 0 ? loginActivity[loginActivity.length - 1]?.activeUsers || 0 : 0}
              </span>
              <span className="analytics-dau-stat-label">Today</span>
            </div>
          </div>
        </div>
      )}

      {/* Activity Heatmap + Enrollment Timeline */}
      <div className="analytics-grid-2">
        {heatmapGrid && (
          <div className="analytics-section" style={{ marginBottom: 0 }}>
            <div className="analytics-section-title">
              <Grid3X3 size={20} className="analytics-section-icon" />
              Activity Heatmap
            </div>
            <div className="analytics-section-subtitle">When users are most active (last 30 days)</div>
            <div className="analytics-heatmap-container">
              <div className="analytics-heatmap-header">
                <div className="analytics-heatmap-corner" />
                {heatmapGrid.hours.map(h => (
                  <div key={h} className="analytics-heatmap-hour-label">
                    {h > 12 ? `${h - 12}p` : h === 12 ? '12p' : `${h}a`}
                  </div>
                ))}
              </div>
              {heatmapGrid.grid.map((row, ri) => (
                <div key={ri} className="analytics-heatmap-row">
                  <div className="analytics-heatmap-day-label">{heatmapGrid.days[ri]}</div>
                  {row.map((cell, ci) => (
                    <div key={ci} className="analytics-heatmap-cell"
                      style={{ background: cell.intensity > 0 ? `rgba(99, 102, 241, ${0.1 + cell.intensity * 0.8})` : 'var(--color-bg, #f3f4f6)' }}
                      title={`${cell.day} ${cell.hour}:00 - ${cell.count} logins`}
                    >
                      {cell.count > 0 && <span className="analytics-heatmap-count">{cell.count}</span>}
                    </div>
                  ))}
                </div>
              ))}
              <div className="analytics-heatmap-legend">
                <span>Less</span>
                <div className="analytics-heatmap-legend-scale">
                  {[0.1, 0.3, 0.5, 0.7, 0.9].map((opacity, i) => (
                    <div key={i} className="analytics-heatmap-legend-cell" style={{ background: `rgba(99, 102, 241, ${opacity})` }} />
                  ))}
                </div>
                <span>More</span>
              </div>
            </div>
          </div>
        )}

        {enrollmentTimeline.length > 0 && (
          <div className="analytics-section" style={{ marginBottom: 0 }}>
            <div className="analytics-section-title">
              <CalendarDays size={20} className="analytics-section-icon" />
              Enrollment Timeline
            </div>
            <div className="analytics-section-subtitle">Weekly enrollments over the past 6 months</div>
            <div className="analytics-enrollment-timeline">
              {(() => {
                const maxVal = Math.max(...enrollmentTimeline.map(d => Math.max(d.enrollments, d.completions)), 1);
                const displayData = enrollmentTimeline.filter((_, i) => i % 2 === 0 || i === enrollmentTimeline.length - 1);
                return (
                  <div className="analytics-trends-chart" style={{ height: 200 }}>
                    {displayData.map((entry, i) => {
                      const enrollPct = (entry.enrollments / maxVal) * 100;
                      const completePct = (entry.completions / maxVal) * 100;
                      return (
                        <div key={i} className="analytics-trend-bar-group">
                          <div className="analytics-trend-bars">
                            <div className="analytics-trend-bar enrolled" style={{ height: `${enrollPct}%` }} title={`Enrollments: ${entry.enrollments}`}>
                              <span className="analytics-trend-bar-value">{entry.enrollments}</span>
                            </div>
                            <div className="analytics-trend-bar completed" style={{ height: `${completePct}%` }} title={`Completions: ${entry.completions}`}>
                              <span className="analytics-trend-bar-value">{entry.completions}</span>
                            </div>
                          </div>
                          <div className="analytics-trend-label">{entry.week}</div>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </div>
            <div className="analytics-trend-legend">
              <div className="analytics-trend-legend-item">
                <div className="analytics-trend-legend-dot" style={{ background: 'var(--color-primary)' }} />
                Enrollments
              </div>
              <div className="analytics-trend-legend-item">
                <div className="analytics-trend-legend-dot" style={{ background: '#16a34a' }} />
                Completions
              </div>
            </div>
            <div className="analytics-timeline-summary">
              <div className="analytics-dau-stat">
                <span className="analytics-dau-stat-value">{enrollmentTimeline.reduce((s, d) => s + d.enrollments, 0)}</span>
                <span className="analytics-dau-stat-label">Total Enrollments</span>
              </div>
              <div className="analytics-dau-stat">
                <span className="analytics-dau-stat-value">{enrollmentTimeline.reduce((s, d) => s + d.completions, 0)}</span>
                <span className="analytics-dau-stat-label">Total Completions</span>
              </div>
              <div className="analytics-dau-stat">
                <span className="analytics-dau-stat-value">
                  {Math.round(enrollmentTimeline.reduce((s, d) => s + d.enrollments, 0) / Math.max(enrollmentTimeline.length, 1))}
                </span>
                <span className="analytics-dau-stat-label">Avg/Week</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Learning Time Analysis + Engagement Metrics */}
      <div className="analytics-grid-2">
        {chartData && (
          <div className="analytics-section" style={{ marginBottom: 0 }}>
            <div className="analytics-section-title">
              <Clock size={20} className="analytics-section-icon" />
              Learning Time Analysis
            </div>
            <div className="analytics-section-subtitle">Weekly learning hours trend</div>
            <div className="analytics-chart-container">
              <svg viewBox={`0 0 ${chartData.width} ${chartData.height}`} className="analytics-svg-chart" preserveAspectRatio="xMidYMid meet">
                {chartData.yLabels.map((label, i) => (
                  <g key={i}>
                    <line x1={chartData.padding} y1={label.y} x2={chartData.padding + chartData.chartW} y2={label.y} stroke="var(--color-border, #e5e7eb)" strokeWidth="0.5" strokeDasharray="4,4" />
                    <text x={chartData.padding - 8} y={label.y + 4} textAnchor="end" fill="var(--color-secondary-text)" fontSize="10">{label.value}h</text>
                  </g>
                ))}
                <defs>
                  <linearGradient id="timeGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#10b981" stopOpacity="0.3" />
                    <stop offset="100%" stopColor="#10b981" stopOpacity="0.02" />
                  </linearGradient>
                </defs>
                <path d={chartData.areaD} fill="url(#timeGradient)" />
                <path d={chartData.pathD} fill="none" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                {chartData.points.map((p, i) => (
                  <g key={i}>
                    <circle cx={p.x} cy={p.y} r="3.5" fill="#10b981" stroke="white" strokeWidth="1.5" />
                    <text x={p.x} y={chartData.padding + chartData.chartH + 16} textAnchor="middle" fill="var(--color-secondary-text)" fontSize="9">{p.week}</text>
                  </g>
                ))}
              </svg>
            </div>
          </div>
        )}

        {engagement && (
          <div className="analytics-section" style={{ marginBottom: 0 }}>
            <div className="analytics-section-title">
              <Zap size={20} className="analytics-section-icon" />
              Engagement Metrics
            </div>
            <div className="analytics-section-subtitle">Key engagement indicators</div>
            <div className="analytics-engagement-metrics-list">
              <div className="analytics-engagement-metric-row">
                <div className="analytics-engagement-metric-label"><Users size={16} color="#6366f1" /><span>Total Active Users</span></div>
                <span className="analytics-engagement-metric-value">{engagement.totalActiveUsers}</span>
              </div>
              <div className="analytics-engagement-metric-row">
                <div className="analytics-engagement-metric-label"><BookOpen size={16} color="#ff8c42" /><span>Total Enrollments</span></div>
                <span className="analytics-engagement-metric-value">{engagement.totalEnrollments}</span>
              </div>
              <div className="analytics-engagement-metric-row">
                <div className="analytics-engagement-metric-label"><Clock size={16} color="#f59e0b" /><span>Total Learning Time</span></div>
                <span className="analytics-engagement-metric-value">{engagement.totalTimeSpent}h</span>
              </div>
              <div className="analytics-engagement-metric-row">
                <div className="analytics-engagement-metric-label"><BarChart3 size={16} color="#0ea5e9" /><span>Avg Time Per User</span></div>
                <span className="analytics-engagement-metric-value">{engagement.avgTimePerUser}h</span>
              </div>
              <div className="analytics-engagement-metric-row">
                <div className="analytics-engagement-metric-label"><Activity size={16} color="#ec4899" /><span>Active Last 7 Days</span></div>
                <span className="analytics-engagement-metric-value">{engagement.activeUsersLast7Days}</span>
              </div>
              <div className="analytics-engagement-metric-row">
                <div className="analytics-engagement-metric-label"><CheckCircle size={16} color="#16a34a" /><span>Enrollment Rate</span></div>
                <span className="analytics-engagement-metric-value">{insights.enrollmentRate}%</span>
              </div>
              <div className="analytics-engagement-metric-row">
                <div className="analytics-engagement-metric-label"><Target size={16} color="#8b5cf6" /><span>Avg Completion Rate</span></div>
                <span className="analytics-engagement-metric-value">{insights.avgCompletionRate}%</span>
              </div>
              <div className="analytics-engagement-metric-row">
                <div className="analytics-engagement-metric-label"><Award size={16} color="#d97706" /><span>Total Certifications</span></div>
                <span className="analytics-engagement-metric-value">{insights.totalCerts}</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default React.memo(EngagementTab);
